/**
 * 가짜 로컬 커널 호스트(C-05 계약 그대로 — libs/local-host/src/protocol.ts).
 *
 * 실제 호스트(`createLocalHost` — Worker 안 커널·런타임·모드 엔진)가 들어오기 전까지 셸 개발·E2E·PT-02 를
 * 돌리는 대역이다. 물리 대신 단순한 운동 모형(코스 축 +z · 높이 윤곽)으로 움직이고, 계약의 이벤트를 신호대로 낸다:
 *   - stall / stall-cleared : 언덕에서 속도 > 80 이면 바퀴가 미끄러져 막힘(포트 A) · 턱 앞에서 속도 < 35 이면 못 올라감
 *   - respawn               : 길 밖으로 떨어져 y < -1.0(SR-MODE-03) → 시작점 · 리셋 버튼
 *   - finish / fail         : 결승 구역 진입 · 타이머 만료 · 포기
 *   - fe(FE-202)            : 시나리오로(`fe202AtS`)
 * S4(계약 확장):
 *   - 전시관(mode.id ≠ obstacle-course + exhibits): 받침대 격자(한 줄 6) · activate → activated(60초) / activate-busy(6대)
 *     / deactivated · 작동 중 막힘 stall-other(기본: 별명 '쿵쿵' — 와이어 예시)
 *   - 친구 놀이(exhibits 없음 · friends ≤5): 나는 리모컨, 친구는 작은 원을 그리며 스스로(리모컨 이벤트 없음)
 *   - 기록(record ≠ false · 코스): 10Hz 위치 트랙 + 가짜 ReplayFile(./fakeReplay) → exportReplay
 *   - 리플레이 고스트(ghostReplays): 위치 트랙으로 그리고 playback 'input' 을 알린다. `fe501AtS` 면 그때 FE-501 → 'track'
 *   - loadReplay(FS-14): start/pause/setSpeed(0.5·1·2)/seek · playback-end · 빌드가 다르면 처음부터 'track'
 * 리모컨 매핑(커널 계약 · 결정 2026-09-25 최종): 허브 LEFT ← D-패드 서쪽 성분(w·nw·sw) 또는 A,
 * 허브 RIGHT ← 동쪽 성분(e·ne·se) 또는 B, liveValue ← 슬라이더, 북/남 + 슬라이더 0 → ±100.
 * 가짜 '프로그램'은 그 결과로 움직인다: 북/남 = 앞/뒤, 허브 LEFT = 왼쪽으로 틀기, RIGHT = 오른쪽으로 틀기.
 * 정적 전시(program null)는 움직이지 않는다(전시만).
 *
 * 시계: realtime(기본 — setInterval 60Hz) 또는 manual(`advance(ms)` — 단위 시험).
 */
import type {
  BodyDesc,
  FailAnalysis,
  FailPattern,
  Frame,
  FriendMachine,
  HostStats,
  KernelEvent,
  LoadRequest,
  LoadResult,
  PrincipleMachine,
  RemoteInput,
  ReplayExport,
  ReplayLoadRequest,
  ReplayLoadResult,
  ReplayMeta,
  RunBundle,
} from "@picnic-field/local-host/protocol";
import { bytesToTrack, decodeFakeReplay, encodeFakeReplay, trackToBytes } from "./fakeReplay";
import type { SegKind, Segment, CourseLayout, FakeHostOptions, FakeLocalHost } from "../types";

export const TICK_HZ = 60;
export const TICK_MS = 1000 / TICK_HZ;
/** 속도 100% 일 때 최고 속력(m/s) — SPIKE 중형 모터 + 5.6cm 바퀴 근사 */
export const VMAX = 0.45;
export const YAW_RATE = 1.6; // rad/s
export const SLIP_OVER = 80; // 언덕에서 이보다 빠르면 미끄러짐
export const STEP_UNDER = 35; // 턱에서 이보다 느리면 못 오름
export const FALL_Y = -1.0;
const GRAVITY = 9.8;

// ── 코스 윤곽(가짜 콘텐츠 — 실제 프리팹은 커널 모드 엔진 소유) ───────────────────

const W = 0.35; // 기본 반폭(길 폭 70cm)

function layout(key: string): CourseLayout {
  const S: Segment[] = [];
  let z = 0;
  let y = 0;
  const add = (kind: SegKind, len: number, rise = 0, halfWidth = W) => {
    S.push({ kind, z0: z, z1: z + len, y0: y, y1: y + rise, halfWidth });
    z += len;
    y += rise;
  };
  if (key === "easy") {
    add("ground", 1.4);
    add("ramp", 1.0, 0.1); // 약 6°
    add("plateau", 1.0);
  } else if (key === "hard") {
    add("ground", 0.9);
    add("bridge", 1.1, 0, 0.09);
    add("ground", 0.3);
    add("step", 0.25, 0.03);
    add("step", 0.25, 0.03);
    add("ramp", 1.0, 0.27); // 약 15°
    add("plateau", 1.1);
  } else {
    // medium: 계단 + 언덕
    add("ground", 1.1);
    add("step", 0.3, 0.03);
    add("step", 0.3, 0.03);
    add("ramp", 1.0, 0.2); // 약 11°
    add("plateau", 1.1);
  }
  return { segments: S, startZ: [0, 0.35], finishZ: [z - 0.4, z], length: z };
}

export function courseLayout(key: string): CourseLayout {
  return layout(key);
}

export function segmentAt(l: CourseLayout, zz: number): Segment | null {
  for (const s of l.segments) if (zz >= s.z0 && zz < s.z1) return s;
  return null;
}

export function heightAt(s: Segment, zz: number): number {
  if (s.kind === "step") return s.y1; // 턱은 올라서면 바로 높이
  const t = (zz - s.z0) / Math.max(1e-6, s.z1 - s.z0);
  return s.y0 + (s.y1 - s.y0) * t;
}

// ── 쿼터니언 ─────────────────────────────────────────────────────────────

type Quat = [number, number, number, number];
const qYaw = (a: number): Quat => [0, Math.sin(a / 2), 0, Math.cos(a / 2)];
/** 앞(+z)이 들리는 기울기 = -x 축 회전 */
const qPitch = (a: number): Quat => [-Math.sin(a / 2), 0, 0, Math.cos(a / 2)];
function qMul(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

// ── 운동 모형 ────────────────────────────────────────────────────────────

interface Car {
  x: number;
  y: number;
  z: number;
  yaw: number;
  vy: number;
  falling: boolean;
  pitch: number;
}

const spawn = (l: CourseLayout): Car => ({
  x: 0,
  y: 0,
  z: (l.startZ[0] + l.startZ[1]) / 2,
  yaw: 0,
  vy: 0,
  falling: false,
  pitch: 0,
});

/** 리모컨 → 허브 버튼(커널 매핑 흉내): LEFT = 서쪽 성분 또는 A, RIGHT = 동쪽 성분 또는 B */
export function hubButtonsOf(inp: RemoteInput): { left: boolean; right: boolean } {
  const d = inp.dpad ?? "";
  return { left: d.includes("w") || inp.a, right: d.includes("e") || inp.b };
}

/** 입력 → 속도(%) · 회전(-1..1). 가짜 프로그램: 북/남 = 앞/뒤, 허브 LEFT/RIGHT = 틀기(둘 다면 0) */
export function driveOf(inp: RemoteInput): { pct: number; turn: number } {
  const d = inp.dpad ?? "";
  const mag = Math.abs(inp.slider) || 100;
  const pct = d.includes("n") ? mag : d.includes("s") ? -mag : 0;
  const hub = hubButtonsOf(inp);
  const turn = (hub.left ? 1 : 0) - (hub.right ? 1 : 0);
  return { pct, turn };
}

type StallCause = "slip" | "step" | null;

interface StepResult {
  stall: StallCause;
  fell: boolean;
  finished: boolean;
  /** S5: 이번 틱에 길 밖으로 나갔다(추락 시작) — 그때 표면 · 속도(%) · 방향 */
  left?: { surface: SegKind | null; pct: number; yawAbs: number };
}

function stepCar(
  car: Car,
  l: CourseLayout,
  inp: RemoteInput,
  movable: boolean,
  speedScale: number,
  dt: number,
): StepResult {
  if (car.falling) {
    car.vy -= GRAVITY * dt;
    car.y += car.vy * dt;
    return { stall: null, fell: car.y < FALL_Y, finished: false };
  }
  const { pct, turn } = movable ? driveOf(inp) : { pct: 0, turn: 0 };
  car.yaw += turn * YAW_RATE * dt * (pct !== 0 ? 1 : 0.6);
  let v = (pct / 100) * VMAX * speedScale;
  const here = segmentAt(l, car.z);
  const ahead = segmentAt(l, car.z + Math.sign(v) * 0.08);
  let stall: StallCause = null;
  if (pct > 0 && here?.kind === "ramp" && pct > SLIP_OVER) {
    stall = "slip";
    v = 0;
  } else if (pct > 0 && ahead?.kind === "step" && here !== ahead && Math.abs(pct) < STEP_UNDER) {
    stall = "step";
    v = 0;
  }
  car.z += v * Math.cos(car.yaw) * dt;
  car.x += v * Math.sin(car.yaw) * dt;
  car.z = Math.max(car.z, -0.2);
  const seg = segmentAt(l, car.z);
  if (!seg || Math.abs(car.x) > seg.halfWidth) {
    if (car.z >= l.length) return { stall, fell: false, finished: true };
    car.falling = true;
    car.vy = 0;
    const yawAbs = Math.abs(Math.atan2(Math.sin(car.yaw), Math.cos(car.yaw)));
    return {
      stall: null,
      fell: false,
      finished: false,
      left: { surface: (seg ?? here)?.kind ?? null, pct: Math.abs(pct), yawAbs },
    };
  }
  car.y = heightAt(seg, car.z);
  car.pitch =
    seg.kind === "ramp"
      ? Math.atan2(seg.y1 - seg.y0, seg.z1 - seg.z0)
      : seg.kind === "step"
        ? 0
        : 0;
  const finished = car.z >= l.finishZ[0];
  return { stall, fell: false, finished };
}

function writePose(out: Float32Array, i: number, car: Car) {
  const q = qMul(qYaw(car.yaw), qPitch(car.pitch));
  out.set([car.x, car.y, car.z, q[0], q[1], q[2], q[3]], i * 7);
}

// ── 몸체 목록(코스) ────────────────────────────────────────────────────────

interface Built {
  bodies: BodyDesc[];
  statics: Float32Array; // env·zone 자세(한 번 계산)
  meIndex: number;
  ghostIndex: Map<string, number>;
}

function build(l: CourseLayout, ghostIds: readonly string[]): Built {
  const bodies: BodyDesc[] = [];
  const poses: number[][] = [];
  const envBox = (
    surface: string,
    cx: number,
    cy: number,
    cz: number,
    h: [number, number, number],
  ) => {
    bodies.push({
      index: bodies.length,
      kind: "env",
      owner: "env",
      shape: { type: "box", half: h },
      surface,
    });
    poses.push([cx, cy, cz, 0, 0, 0, 1]);
  };
  for (const s of l.segments) {
    const len = s.z1 - s.z0;
    const cz = (s.z0 + s.z1) / 2;
    if (s.kind === "ramp") {
      // C-05 ramp = half 상자 안 쐐기(지역 −z 낮은 끝 밑면 · +z 높은 끝 윗면), 회전 없음 — 실제 모드 엔진과 같은 규칙
      const hy = (s.y1 - s.y0) / 2;
      bodies.push({
        index: bodies.length,
        kind: "env",
        owner: "env",
        shape: {
          type: "ramp",
          half: [s.halfWidth, hy, len / 2],
          angleDeg: (Math.atan2(2 * hy, len) * 180) / Math.PI,
        },
        surface: "ramp",
      });
      poses.push([0, s.y0 + hy, cz, 0, 0, 0, 1]);
      // 언덕 밑 채움(앞 턱 높이까지)
      if (s.y0 > 0)
        envBox("ground", 0, (s.y0 - 0.02) / 2, cz, [s.halfWidth, (s.y0 + 0.02) / 2, len / 2]);
    } else {
      const top = s.y1;
      envBox(s.kind, 0, top / 2 - 0.02, cz, [s.halfWidth, top / 2 + 0.02, len / 2]);
    }
  }
  const zone = (surface: "start" | "finish", z0: number, z1: number, y: number) => {
    bodies.push({
      index: bodies.length,
      kind: "zone",
      owner: "env",
      shape: { type: "box", half: [W, 0.004, (z1 - z0) / 2] },
      surface,
    });
    poses.push([0, y + 0.003, (z0 + z1) / 2, 0, 0, 0, 1]);
  };
  zone("start", l.startZ[0], l.startZ[1], 0);
  const last = l.segments[l.segments.length - 1];
  zone("finish", l.finishZ[0], l.finishZ[1], last ? last.y1 : 0);

  const meIndex = bodies.length;
  bodies.push({ index: meIndex, kind: "chassis", owner: "me" });
  const ghostIndex = new Map<string, number>();
  for (const id of ghostIds) {
    ghostIndex.set(id, bodies.length);
    bodies.push({ index: bodies.length, kind: "ghost", owner: id });
  }
  const statics = new Float32Array(meIndex * 7);
  poses.forEach((p, i) => statics.set(p, i * 7));
  return { bodies, statics, meIndex, ghostIndex };
}

// ── 전시관(받침대 ≤30) · 친구 놀이(나 + 친구 ≤5) ─────────────────────────────

/** 전시관 받침대 배치 — 한 줄 6개, 옆 0.9m · 줄 사이 1.1m, 입구는 남쪽(−z). SPIKE 작품(20~40cm)에 맞춘 받침대 */
export const GALLERY_COLS = 6;
export const PEDESTAL_DX = 0.9;
export const PEDESTAL_DZ = 1.1;
export const PEDESTAL_HALF: [number, number, number] = [0.2, 0.12, 0.2];
/** 동시 작동 한도(SR-MODE-02 기본 6대) · 작동 시간(60초) */
export const ACTIVE_LIMIT = 6;
export const ACTIVE_SECONDS = 60;
/** 친구 놀이 바닥 반폭(m) */
export const PLAY_HALF = 2.4;

export function galleryLayout(n: number): { x: number; z: number }[] {
  const out: { x: number; z: number }[] = [];
  const cols = Math.min(GALLERY_COLS, Math.max(1, n));
  for (let i = 0; i < n; i++) {
    const r = Math.floor(i / GALLERY_COLS);
    const c = i % GALLERY_COLS;
    // 화면 왼쪽부터(카메라가 북쪽을 보면 화면 왼쪽 = +x)
    out.push({ x: ((cols - 1) / 2 - c) * PEDESTAL_DX, z: 1 + r * PEDESTAL_DZ });
  }
  return out;
}

interface Mover {
  id: string;
  label: string;
  index: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** 작동·배회 기준 자세 */
  x0: number;
  z0: number;
  yaw0: number;
  movable: boolean;
  /** 전시 작품: 작동이 끝나는 틱(0 = 멈춤) */
  activeUntil: number;
  stallAt: number | null;
  stalled: boolean;
  /** 친구: 배회 반지름 · 위상 */
  radius: number;
  phase: number;
}

interface World {
  bodies: BodyDesc[];
  statics: Float32Array;
  movers: Mover[];
  /** 친구 놀이의 내 기계(없으면 null) */
  me: Mover | null;
}

function envBody(
  bodies: BodyDesc[],
  poses: number[][],
  b: Omit<BodyDesc, "index">,
  pose: number[],
) {
  bodies.push({ ...b, index: bodies.length } as BodyDesc);
  poses.push(pose);
}

/** 모드 정의의 받침대 자리(gallery.pedestals[].at) — 없으면 null */
function modeSlots(mode: unknown): { x: number; z: number }[] | null {
  const g = (mode as { gallery?: { pedestals?: { at?: number[] }[] } }).gallery;
  const list = g?.pedestals?.map((p) => ({ x: p.at?.[0] ?? 0, z: p.at?.[2] ?? 0 })) ?? [];
  return list.length ? list : null;
}

function buildGallery(
  exhibits: readonly FriendMachine[],
  slots: readonly { x: number; z: number }[] | null = null,
): World {
  const bodies: BodyDesc[] = [];
  const poses: number[][] = [];
  const spots =
    slots && slots.length >= exhibits.length
      ? slots.slice(0, exhibits.length)
      : galleryLayout(exhibits.length);
  const xs = spots.map((s) => s.x);
  const zs = spots.map((s) => s.z);
  const hx = Math.max(2, (Math.max(0, ...xs) - Math.min(0, ...xs)) / 2 + 1.2);
  // 바닥 가운데 — 받침대가 한쪽에만 있어도(실제 전시관 자리 · ?fs03=slots) 바닥이 덮는다
  const cx = (Math.max(0, ...xs) + Math.min(0, ...xs)) / 2;
  const z1 = Math.max(3, ...zs) + 1.2;
  envBody(
    bodies,
    poses,
    {
      kind: "env",
      owner: "env",
      shape: { type: "box", half: [hx, 0.02, (z1 + 1.2) / 2] },
      surface: "ground",
    },
    [cx, -0.02, (z1 - 1.2) / 2, 0, 0, 0, 1],
  );
  exhibits.forEach((e, i) => {
    const s = spots[i]!;
    envBody(
      bodies,
      poses,
      {
        kind: "env",
        owner: e.id,
        shape: { type: "box", half: PEDESTAL_HALF },
        surface: "pedestal",
      },
      [s.x, PEDESTAL_HALF[1], s.z, 0, 0, 0, 1],
    );
  });
  const movers: Mover[] = [];
  exhibits.forEach((e, i) => {
    const s = spots[i]!;
    const index = bodies.length;
    bodies.push({ index, kind: "chassis", owner: e.id });
    movers.push({
      id: e.id,
      label: e.label,
      index,
      x: s.x,
      y: PEDESTAL_HALF[1] * 2,
      z: s.z,
      yaw: Math.PI, // 입구(남쪽)를 본다
      x0: s.x,
      z0: s.z,
      yaw0: Math.PI,
      movable: hasProgram(e.bundle),
      activeUntil: 0,
      stallAt: null,
      stalled: false,
      radius: 0,
      phase: 0,
    });
  });
  const statics = new Float32Array(bodies.length * 7);
  poses.forEach((p, i) => statics.set(p, i * 7));
  return { bodies, statics, movers, me: null };
}

function buildPlay(me: RunBundle | undefined, friends: readonly FriendMachine[]): World {
  const bodies: BodyDesc[] = [];
  const poses: number[][] = [];
  envBody(
    bodies,
    poses,
    {
      kind: "env",
      owner: "env",
      shape: { type: "box", half: [PLAY_HALF, 0.02, PLAY_HALF] },
      surface: "ground",
    },
    [0, -0.02, 0, 0, 0, 0, 1],
  );
  const movers: Mover[] = [];
  let meMover: Mover | null = null;
  if (me) {
    const index = bodies.length;
    bodies.push({ index, kind: "chassis", owner: "me" });
    meMover = {
      id: "me",
      label: "me",
      index,
      x: 0,
      y: 0,
      z: -1.2,
      yaw: 0,
      x0: 0,
      z0: -1.2,
      yaw0: 0,
      movable: hasProgram(me),
      activeUntil: 0,
      stallAt: null,
      stalled: false,
      radius: 0,
      phase: 0,
    };
  }
  friends.forEach((f, i) => {
    const index = bodies.length;
    bodies.push({ index, kind: "chassis", owner: f.id });
    const a = (i / Math.max(1, friends.length)) * Math.PI * 2;
    movers.push({
      id: f.id,
      label: f.label,
      index,
      x: Math.cos(a) * 1.4,
      y: 0,
      z: 0.4 + Math.sin(a) * 1.4,
      yaw: a,
      x0: Math.cos(a) * 1.4,
      z0: 0.4 + Math.sin(a) * 1.4,
      yaw0: a,
      movable: hasProgram(f.bundle),
      activeUntil: 0,
      stallAt: null,
      stalled: false,
      radius: 0.35 + (i % 3) * 0.15,
      phase: a,
    });
  });
  const statics = new Float32Array(bodies.length * 7);
  poses.forEach((p, i) => statics.set(p, i * 7));
  return { bodies, statics, movers, me: meMover };
}

function writeMover(out: Float32Array, m: Mover) {
  const q = qYaw(m.yaw);
  out.set([m.x, m.y, m.z, q[0], q[1], q[2], q[3]], m.index * 7);
}

// ── 호스트 ───────────────────────────────────────────────────────────────

const NEUTRAL: RemoteInput = { dpad: null, a: false, b: false, slider: 0 };

type Kind = FakeLocalHost["kind"];

export function createFakeHost(opts: FakeHostOptions = {}): FakeLocalHost {
  const speedScale = opts.speedScale ?? 1;
  const limit = opts.activeLimit ?? ACTIVE_LIMIT;
  const stallExhibit = opts.stallExhibit ?? ((_o: string, label: string) => label === "쿵쿵");
  const frameCbs = new Set<(f: Frame) => void>();
  const eventCbs = new Set<(e: KernelEvent) => void>();
  const tickCost: number[] = [];

  let kind: Kind = "none";
  // 코스
  let l: CourseLayout | null = null;
  let built: Built | null = null;
  let ghosts: { ghostId: string; hz: number; frames: Float32Array; replay: boolean }[] = [];
  let movable = false;
  let timerS = 0;
  let car: Car | null = null;
  let record = true;
  let meBundle: RunBundle | null = null;
  let lastReq: LoadRequest | null = null;
  let track: number[] = [];
  let outcome: "complete" | "incomplete" | null = null;
  let finishMs: number | null = null;
  let ghostSwitched = false;
  // 전시관 · 친구 놀이
  let world: World | null = null;
  // 리플레이
  let replay: {
    meta: ReplayMeta;
    frames: Float32Array;
    pos: number; // 틱(소수)
    playback: "input" | "track";
    ended: boolean;
  } | null = null;
  let speed: 0.5 | 1 | 2 = 1;

  let tick = 0;
  let runTick = 0; // 판정 시간(리셋해도 흐른다 — 시간 = 한 판 전체)
  let attempts = 1;
  let input: RemoteInput = NEUTRAL;
  let prevButtons = { a: false, b: false };
  let running = false;
  let paused = false;
  let over = false;
  let stalled: string | null = null;
  /** S5 실패 분석 — 마지막 막힘·추락(판 끝 fail 에 싣는다) · 떨어지는 중인 추락 */
  let lastFail: FailAnalysis | null = null;
  let falling: FailAnalysis | null = null;
  let fe202Sent = false;
  let interval: ReturnType<typeof setInterval> | null = null;
  let acc = 0;
  let lastWall = 0;
  let disposed = false;

  const emit = (e: KernelEvent) => eventCbs.forEach((cb) => cb(e));

  const ghostPose = (tf: Float32Array, gi: number, frames: Float32Array, hz: number, t: number) => {
    const per = Math.floor(frames.length / 7);
    if (per <= 0) return;
    // 10Hz 트랙을 틱 시간에 맞춰 선형 보간(자세는 가까운 쪽)
    const ft = (t / TICK_HZ) * hz;
    const i0 = Math.min(per - 1, Math.floor(ft));
    const i1 = Math.min(per - 1, i0 + 1);
    const a = Math.min(1, ft - i0);
    for (let k = 0; k < 7; k++) {
      const v0 = frames[i0 * 7 + k] ?? 0;
      const v1 = frames[i1 * 7 + k] ?? 0;
      tf[gi * 7 + k] = k < 3 ? v0 + (v1 - v0) * a : a < 0.5 ? v0 : v1;
    }
  };

  /** P2-S5 원리 보기(가짜) — 켰을 때만 frame 에 싣는다 */
  let principleOn = false;
  const withPrinciple = (f: Frame, tf: Float32Array, who: [string, number, number][]): Frame =>
    principleOn ? { ...f, principle: who.map(([o, i, p]) => fakePrinciple(o, tf, i, p)) } : f;

  const frame = (): Frame | null => {
    if ((kind === "course" || kind === "replay") && built) {
      const tf = new Float32Array(built.bodies.length * 7);
      tf.set(built.statics);
      if (kind === "replay" && replay) {
        const at = Math.min(replay.pos, replay.meta.durationTicks);
        if (replay.frames.length >= 7) ghostPose(tf, built.meIndex, replay.frames, 10, at);
        else if (car) writePose(tf, built.meIndex, car);
        return {
          tick: Math.floor(at),
          timeMs: (Math.floor(at) * 1000) / TICK_HZ,
          transforms: tf,
          remainingS: null,
        };
      }
      if (!car) return null;
      writePose(tf, built.meIndex, car);
      for (const g of ghosts) {
        const gi = built.ghostIndex.get(g.ghostId);
        if (gi != null) ghostPose(tf, gi, g.frames, g.hz, runTick);
      }
      return withPrinciple(
        {
          tick,
          timeMs: (runTick * 1000) / TICK_HZ,
          transforms: tf,
          remainingS: timerS > 0 ? Math.max(0, timerS - runTick / TICK_HZ) : null,
        },
        tf,
        [["me", built.meIndex, running && movable ? driveOf(input).pct : 0]],
      );
    }
    if ((kind === "gallery" || kind === "play") && world) {
      const tf = new Float32Array(world.bodies.length * 7);
      tf.set(world.statics);
      for (const m of world.movers) writeMover(tf, m);
      if (world.me) writeMover(tf, world.me);
      const who: [string, number, number][] = [];
      if (world.me) who.push(["me", world.me.index, running ? driveOf(input).pct : 0]);
      for (const m of world.movers)
        if (kind === "play" || m.activeUntil) who.push([m.id, m.index, m.movable ? 40 : 0]);
      return withPrinciple(
        { tick, timeMs: (tick * 1000) / TICK_HZ, transforms: tf, remainingS: null },
        tf,
        who,
      );
    }
    return null;
  };

  const clearStall = () => {
    if (stalled) emit({ type: "stall-cleared", port: stalled });
    stalled = null;
  };

  /** 커널 판정 흉내(SR-ACH-04 — 실패 지점·속도·기울기·모터 부하) */
  const analyze = (pattern: FailPattern, pct: number, motorLoad: number): FailAnalysis => {
    const c = car;
    const seg = l && c ? segmentAt(l, c.z) : null;
    return {
      pattern,
      progress: l && c ? Math.max(0, Math.min(1, c.z / l.length)) : 0,
      surface: seg?.kind ?? null,
      speedMps: Math.round((Math.abs(pct) / 100) * VMAX * speedScale * 100) / 100,
      tiltDeg: c ? Math.round((c.pitch * 180) / Math.PI) : 0,
      motorLoad,
    };
  };

  const respawn = (reason: "fall" | "reset") => {
    if (!l) return;
    clearStall();
    attempts += 1;
    const analysis = reason === "fall" ? (falling ?? undefined) : undefined;
    if (analysis) lastFail = analysis;
    falling = null;
    car = spawn(l);
    emit(
      analysis
        ? { type: "respawn", attempt: attempts, reason, analysis }
        : { type: "respawn", attempt: attempts, reason },
    );
  };

  const finish = (e: KernelEvent) => {
    over = true;
    running = false;
    clearStall();
    if (e.type === "finish") {
      outcome = "complete";
      finishMs = e.timeMs;
    } else outcome = "incomplete";
    emit(e);
  };

  const stepCourse = () => {
    if (!l || !car) return;
    runTick += 1;
    // 허브 버튼이 눌린 순간 허브 표시(가짜 — 실제는 프로그램이 정한다)
    const hub = hubButtonsOf(input);
    if (hub.left && !prevButtons.a) emit({ type: "hub", owner: "me", light: "left" });
    if (hub.right && !prevButtons.b) emit({ type: "hub", owner: "me", light: "right" });
    prevButtons = { a: hub.left, b: hub.right };
    const r = stepCar(car, l, input, movable, speedScale, 1 / TICK_HZ);
    const cause = r.stall;
    if (cause && !stalled) {
      stalled = "A";
      // 언덕에서 빠르게 출발 → 미끄러짐(slip-start) · 턱 앞에서 느림 → 턱에서 멈춤(stuck-step)
      lastFail = analyze(cause === "slip" ? "slip-start" : "stuck-step", driveOf(input).pct, 1);
      emit({ type: "stall", port: "A" });
    } else if (!cause && stalled) clearStall();
    if (r.left && !falling) {
      // 다리·빠른 속도에서 떨어짐 → 과속 추락 · 방향이 틀어져 길 밖 → 방향 이탈
      const pattern: FailPattern =
        r.left.surface === "bridge" || (r.left.pct >= 70 && r.left.yawAbs <= 0.35)
          ? "overspeed-fall"
          : "off-course";
      falling = { ...analyze(pattern, r.left.pct, 0.3), surface: r.left.surface };
    }
    if (opts.fe202AtS != null && !fe202Sent && runTick / TICK_HZ >= opts.fe202AtS) {
      fe202Sent = true;
      emit({ type: "fe", code: "FE-202" });
    }
    const g = ghosts.find((x) => x.replay);
    if (g && opts.fe501AtS != null && !ghostSwitched && runTick / TICK_HZ >= opts.fe501AtS) {
      ghostSwitched = true;
      emit({ type: "fe", code: "FE-501", detail: g.ghostId });
      emit({ type: "playback", owner: g.ghostId, mode: "track" });
    }
    if (record && runTick % (TICK_HZ / 10) === 0) {
      const p = new Float32Array(7);
      writePose(p, 0, car);
      track.push(...p);
    }
    if (r.fell) respawn("fall");
    else if (r.finished)
      finish({ type: "finish", timeMs: Math.round((runTick * 1000) / TICK_HZ), attempts });
    else if (timerS > 0 && runTick / TICK_HZ >= timerS)
      finish({
        type: "fail",
        reason: "timeout",
        attempts,
        analysis: lastFail ?? analyze("slow-timeout", driveOf(input).pct, 0.2),
      });
  };

  const stepWorld = () => {
    if (!world) return;
    const dt = 1 / TICK_HZ;
    for (const m of world.movers) {
      if (kind === "gallery") {
        if (!m.activeUntil) continue;
        const t = (tick - (m.activeUntil - ACTIVE_SECONDS * TICK_HZ)) / TICK_HZ;
        if (!m.stalled) {
          // 작동: 제자리에서 좌우로 흔들며 까딱(가짜 '프로그램')
          m.yaw = m.yaw0 + Math.sin(t * Math.PI) * 0.6;
          m.y = PEDESTAL_HALF[1] * 2 + Math.abs(Math.sin(t * Math.PI * 2)) * 0.02;
        }
        if (m.stallAt != null && !m.stalled && tick >= m.stallAt) {
          m.stalled = true;
          emit({ type: "stall-other", owner: m.id, port: "B", cleared: false });
        }
        if (tick >= m.activeUntil) {
          if (m.stalled) emit({ type: "stall-other", owner: m.id, port: "B", cleared: true });
          m.activeUntil = 0;
          m.stalled = false;
          m.stallAt = null;
          m.yaw = m.yaw0;
          m.y = PEDESTAL_HALF[1] * 2;
          emit({ type: "deactivated", owner: m.id, reason: "timeout" });
        }
      } else if (m.movable) {
        // 친구: 자기 프로그램대로(가짜 — 작은 원을 그리며 배회), 리모컨 이벤트 없음(SR-LCL-02)
        m.phase += dt * 0.8;
        m.x = m.x0 + Math.cos(m.phase) * m.radius;
        m.z = m.z0 + Math.sin(m.phase) * m.radius;
        m.yaw = -m.phase;
      }
    }
    const me = world.me;
    if (kind === "play" && me && me.movable) {
      const { pct, turn } = driveOf(input);
      me.yaw += turn * YAW_RATE * dt * (pct !== 0 ? 1 : 0.6);
      const v = (pct / 100) * VMAX * speedScale;
      me.x = Math.max(
        -PLAY_HALF + 0.1,
        Math.min(PLAY_HALF - 0.1, me.x + v * Math.sin(me.yaw) * dt),
      );
      me.z = Math.max(
        -PLAY_HALF + 0.1,
        Math.min(PLAY_HALF - 0.1, me.z + v * Math.cos(me.yaw) * dt),
      );
    }
  };

  const stepReplay = () => {
    if (!replay || replay.ended) return;
    replay.pos += speed;
    if (
      opts.fe501AtS != null &&
      replay.playback === "input" &&
      replay.pos / TICK_HZ >= opts.fe501AtS
    ) {
      replay.playback = "track";
      emit({ type: "fe", code: "FE-501", detail: "me" });
      emit({ type: "playback", owner: "me", mode: "track" });
    }
    if (replay.pos >= replay.meta.durationTicks) {
      replay.pos = replay.meta.durationTicks;
      replay.ended = true;
      running = false;
      emit({ type: "playback-end", tick: replay.meta.durationTicks });
    }
  };

  const step = (): boolean => {
    if (!running || paused || over || kind === "none") return false;
    const t0 = performance.now();
    tick += 1;
    if (kind === "course") stepCourse();
    else if (kind === "replay") stepReplay();
    else stepWorld();
    const busy = opts.busyMsPerTick ?? 0;
    if (busy > 0) {
      const until = performance.now() + busy;
      while (performance.now() < until) {
        /* 부하 흉내 */
      }
    }
    tickCost.push(performance.now() - t0);
    if (tickCost.length > 2000) tickCost.shift();
    return true;
  };

  const publish = () => {
    const f = frame();
    if (f) frameCbs.forEach((cb) => cb(f));
  };

  const advance = (ms: number) => {
    acc += ms;
    let n = 0;
    while (acc >= TICK_MS - 1e-9) {
      acc -= TICK_MS;
      if (step()) n += 1;
    }
    if (n > 0) publish();
  };

  const startLoop = () => {
    if (opts.clock === "manual" || interval) return;
    lastWall = performance.now();
    interval = setInterval(() => {
      const now = performance.now();
      // 탭이 멈췄다 돌아오면 따라잡기는 최대 4틱
      advance(Math.min(now - lastWall, TICK_MS * 4));
      lastWall = now;
    }, TICK_MS);
  };

  const stopLoop = () => {
    if (interval) clearInterval(interval);
    interval = null;
  };

  const resetClock = () => {
    tick = 0;
    runTick = 0;
    attempts = 1;
    input = NEUTRAL;
    prevButtons = { a: false, b: false };
    running = false;
    paused = false;
    over = false;
    stalled = null;
    lastFail = null;
    falling = null;
    fe202Sent = false;
    ghostSwitched = false;
    acc = 0;
    tickCost.length = 0;
    track = [];
    outcome = null;
    finishMs = null;
  };

  const host: FakeLocalHost = {
    get layout() {
      return l;
    },
    get lastInput() {
      return input;
    },
    get kind() {
      return kind;
    },
    get speed() {
      return speed;
    },
    async load(req: LoadRequest): Promise<LoadResult> {
      if (disposed) throw new Error("disposed");
      stopLoop();
      resetClock();
      lastReq = req;
      meBundle = req.me ?? null;
      replay = null;
      if (req.mode.id !== COURSE_MODE) {
        // 전시관(받침대) 또는 친구 놀이(나 + 친구) — 기계끼리 충돌 없음
        l = null;
        built = null;
        car = null;
        record = false;
        const exhibits = (req.exhibits ?? []).slice(0, 30);
        if (exhibits.length) {
          kind = "gallery";
          const slots = opts.gallerySlots ? modeSlots(req.mode) : null;
          world = buildGallery(exhibits, slots);
        } else {
          kind = "play";
          world = buildPlay(
            req.me,
            (req.friends ?? []).slice(0, req.runtimeConfig.local.maxFriends),
          );
        }
        timerS = 0;
        queueMicrotask(publish);
        const w = world;
        return {
          bodies: w.bodies,
          followIndex: w.me?.index ?? w.movers[0]?.index ?? 0,
          ports: w.me?.movable ? ["A", "B"] : [],
          timerS: 0,
        };
      }
      kind = "course";
      world = null;
      const preset = req.mode.presets.find((p) => p.key === req.presetKey) ?? req.mode.presets[0];
      l = layout(preset?.key ?? "medium");
      const max = req.runtimeConfig.local.maxGhosts;
      const all = [
        ...(req.ghosts ?? []).map((g) => ({
          ghostId: g.ghostId,
          hz: g.hz,
          frames: g.frames,
          replay: false,
        })),
        ...(req.ghostReplays ?? []).map((g) => ({
          ghostId: g.ghostId,
          hz: 10,
          frames: bytesToTrack(g.positionTrack),
          replay: true,
        })),
      ];
      ghosts = all.slice(0, max);
      built = build(
        l,
        ghosts.map((g) => g.ghostId),
      );
      movable = req.me ? hasProgram(req.me) : false;
      record = req.record !== false;
      timerS = opts.timerOverrideS ?? preset?.timerS ?? req.mode.timer?.seconds ?? 0;
      car = spawn(l);
      if (record) {
        const p0 = new Float32Array(7);
        writePose(p0, 0, car);
        track.push(...p0);
      }
      // 첫 프레임(시작 전 모습 — FS-04-01 미리보기) · 리플레이 고스트는 입력 재생으로 시작
      queueMicrotask(() => {
        publish();
        for (const g of ghosts)
          if (g.replay) emit({ type: "playback", owner: g.ghostId, mode: "input" });
      });
      return {
        bodies: built.bodies,
        followIndex: built.meIndex,
        ports: movable ? ["A", "B"] : [],
        timerS,
      };
    },
    start() {
      if (kind === "none" || over) return;
      if (kind === "replay" && replay?.ended) return;
      running = true;
      paused = false;
      startLoop();
    },
    pause() {
      paused = true;
    },
    resume() {
      paused = false;
      lastWall = performance.now();
    },
    reset() {
      if (kind !== "course" || !running || over) return;
      respawn("reset");
      publish();
    },
    giveUp() {
      if (kind !== "course" || !built || over) return;
      finish({
        type: "fail",
        reason: "giveup",
        attempts,
        analysis: lastFail ?? analyze("unknown", driveOf(input).pct, 0),
      });
      publish();
    },
    input(state: RemoteInput) {
      input = state;
    },
    onFrame(cb) {
      frameCbs.add(cb);
      return () => frameCbs.delete(cb);
    },
    onEvent(cb) {
      eventCbs.add(cb);
      return () => eventCbs.delete(cb);
    },
    async stats(): Promise<HostStats> {
      return {
        ticks: tick,
        tickP50Ms: pct(tickCost, 50),
        tickP95Ms: pct(tickCost, 95),
        transport: "transfer",
      };
    },
    activate(owner: string) {
      if (kind !== "gallery" || !world) return;
      const m = world.movers.find((x) => x.id === owner);
      if (!m || !m.movable) return;
      if (m.activeUntil) {
        // 이미 작동 중 — 남은 시간 그대로 다시 알린다
        emit({ type: "activated", owner, seconds: Math.ceil((m.activeUntil - tick) / TICK_HZ) });
        return;
      }
      const active = world.movers.filter((x) => x.activeUntil).length;
      if (active >= limit) {
        emit({ type: "activate-busy", owner, limit });
        return;
      }
      m.activeUntil = tick + ACTIVE_SECONDS * TICK_HZ;
      m.stallAt = stallExhibit(m.id, m.label) ? tick + 2 * TICK_HZ : null;
      emit({ type: "activated", owner, seconds: ACTIVE_SECONDS });
      if (!running) host.start();
    },
    deactivate(owner: string) {
      if (kind !== "gallery" || !world) return;
      const m = world.movers.find((x) => x.id === owner);
      if (!m || !m.activeUntil) return;
      if (m.stalled) emit({ type: "stall-other", owner: m.id, port: "B", cleared: true });
      m.activeUntil = 0;
      m.stalled = false;
      m.stallAt = null;
      m.yaw = m.yaw0;
      m.y = PEDESTAL_HALF[1] * 2;
      emit({ type: "deactivated", owner: m.id, reason: "manual" });
    },
    async exportReplay(): Promise<ReplayExport | null> {
      if (kind !== "course" || !record || !over || !outcome || !meBundle || !lastReq) return null;
      const meta: ReplayMeta = {
        format: "field-replay@1",
        modeId: lastReq.mode.id,
        modeVersion: lastReq.mode.version,
        presetKey: lastReq.presetKey,
        seed: lastReq.seed >>> 0,
        exactTime: true,
        tickHz: 60,
        durationTicks: runTick,
        snapshotIds: [meBundle.snapshotId],
        versions: meBundle.versions,
        outcome,
        attempts,
        timeMs: outcome === "complete" ? finishMs : null,
        // S5: 현실감 판은 리플레이에 남긴다(커널 ReplayMeta.realism)
        ...(lastReq.realism ? { realism: true as const } : {}),
      };
      return {
        meta,
        file: encodeFakeReplay(meta),
        positionTrack: trackToBytes(new Float32Array(track)),
      };
    },
    async loadReplay(req: ReplayLoadRequest): Promise<ReplayLoadResult> {
      if (disposed) throw new Error("disposed");
      stopLoop();
      resetClock();
      const meta = decodeFakeReplay(req.file);
      if (!meta) throw new Error("fake: not a replay file");
      kind = "replay";
      world = null;
      record = false;
      l = layout(meta.presetKey);
      built = build(l, []);
      car = spawn(l);
      const sameBuild = req.bundles[0]?.versions.engineBuild === meta.versions.engineBuild;
      const playback: "input" | "track" = sameBuild ? "input" : "track";
      replay = { meta, frames: bytesToTrack(req.positionTrack), pos: 0, playback, ended: false };
      speed = 1;
      queueMicrotask(() => {
        publish();
        emit({ type: "playback", owner: "me", mode: playback });
      });
      return {
        bodies: built.bodies,
        followIndex: built.meIndex,
        ports: [],
        timerS: 0,
        meta,
        playback,
      };
    },
    setSpeed(s) {
      speed = s;
    },
    setPrinciple(on: boolean) {
      principleOn = on;
      if (!running) publish();
    },
    seek(t: number) {
      if (kind !== "replay" || !replay) return;
      replay.pos = Math.max(0, Math.min(replay.meta.durationTicks, Math.round(t)));
      replay.ended = replay.pos >= replay.meta.durationTicks;
      publish();
    },
    advance,
    dispose() {
      disposed = true;
      stopLoop();
      frameCbs.clear();
      eventCbs.clear();
    },
  };
  return host;
}

const COURSE_MODE = "obstacle-course";

/**
 * P2-S5 가짜 원리 보기 — 차체 자세(tf)와 명령(%)에서 그럴듯한 값: 무게중심(차체 위 5cm) · 뒷바퀴 구동 토크 · 네 바퀴 바닥 반력 ·
 * 구동 축 회전(모터 %× 10 도/s). 실제 값은 커널(StateExport principle).
 */
export function fakePrinciple(
  owner: string,
  tf: Float32Array,
  index: number,
  pct: number,
): PrincipleMachine {
  const o = index * 7;
  const x = tf[o] ?? 0;
  const y = tf[o + 1] ?? 0;
  const z = tf[o + 2] ?? 0;
  const massKg = 0.5;
  const wheel = (dx: number, dz: number): [number, number, number] => [x + dx, y + 0.03, z + dz];
  const corners: [number, number][] = [
    [-0.05, -0.07],
    [0.05, -0.07],
    [-0.05, 0.07],
    [0.05, 0.07],
  ];
  const torque = pct === 0 ? 0 : Math.sign(pct) * Math.min(0.18, 0.02 + Math.abs(pct) / 1000);
  return {
    owner,
    com: [x, y + 0.05, z],
    massKg,
    joints:
      pct === 0
        ? []
        : [
            {
              id: "drive",
              port: "A",
              kind: "turn",
              at: wheel(-0.06, -0.07),
              axis: [1, 0, 0],
              rate: pct * 10,
            },
          ],
    wheels: corners.map(([dx, dz], i) => ({
      id: `w${i}`,
      port: i < 2 ? "A" : null,
      at: wheel(dx, dz),
      axis: [1, 0, 0] as const,
      spin: pct / 10,
      torque: i < 2 ? torque / 2 : 0,
    })),
    contacts: corners.map(([dx, dz], i) => ({
      body: `w${i}`,
      at: [x + dx, y, z + dz] as const,
      force: [0, (massKg * 9.81) / 4, 0] as const,
    })),
  };
}

function pct(xs: readonly number[], p: number): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.max(0, Math.ceil((p / 100) * s.length) - 1))] ?? 0;
}

export function hasProgram(b: RunBundle): boolean {
  return !b.static && b.program != null;
}

// ── 가짜 고스트 트랙(친구 리플레이 흉내 — S4 부터 리플레이 API) ──────────────────

/**
 * 스크립트 입력으로 가짜 운동 모형을 돌려 10Hz 위치 트랙을 만든다. `stopAtS` 가 있으면 그때부터 멈춤(미완주).
 */
export function simulateTrack(
  presetKey: string,
  script: { slider: number; lane?: number; stopAtS?: number; maxS?: number },
): { frames: Float32Array; completed: boolean } {
  const lay = layout(presetKey);
  const car = spawn(lay);
  car.x = script.lane ?? 0;
  const out: number[] = [];
  const pose = new Float32Array(7);
  const maxTicks = (script.maxS ?? 120) * TICK_HZ;
  let completed = false;
  for (let t = 0; t <= maxTicks; t++) {
    const moving = script.stopAtS == null || t / TICK_HZ < script.stopAtS;
    const inp: RemoteInput = {
      dpad: moving ? "n" : null,
      a: false,
      b: false,
      slider: script.slider,
    };
    if (t % (TICK_HZ / 10) === 0) {
      writePose(pose, 0, car);
      out.push(...pose);
    }
    // 차선 유지(좁은 다리에서는 가운데로) — 가짜 친구는 떨어지지 않는다
    const seg = segmentAt(lay, car.z);
    const lim = Math.max(0, (seg?.halfWidth ?? W) - 0.03);
    car.x = Math.max(-lim, Math.min(lim, script.lane ?? 0));
    const r = stepCar(car, lay, inp, true, 1, 1 / TICK_HZ);
    if (r.fell) break;
    if (r.finished) {
      completed = true;
      writePose(pose, 0, car);
      out.push(...pose);
      break;
    }
    if (!moving && t / TICK_HZ > (script.stopAtS ?? 0) + 3) break;
  }
  return { frames: new Float32Array(out), completed };
}
